import React, {useCallback} from 'react';
import {Button, Card, Form, Input, message} from "antd";
import {UserOutlined} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import {LauncherLayout} from "../layout/LauncherLayout";
import {Label} from "../../commons/translations/Label";
import PhoneInput, {PhoneNumber} from "antd-phone-input";
import {checkNumber} from "../sms/sms-service";
import {useTranslation} from "react-i18next";
import {ContactRequest} from "./types";
import {phoneToString} from "../common/phone-tools";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useLocaleContext} from "../../commons/translations/LocaleContext";
import elGR from "antd/locale/el_GR";
import axios, {AxiosResponse} from "axios";
import {LoadingOverlay} from "../common/loading/component";

export const Contact: React.FC = () => {
    type FormInput = {
        name: string;
        mobile: PhoneNumber;
        message: string;
    }

    const { executeRecaptcha } = useGoogleReCaptcha();
    const [form] = Form.useForm<FormInput>();
    const {locale} = useLocaleContext();
    const [working, setWorking] = React.useState(false);

    const onFinish = useCallback((values: FormInput) => {
        if (!executeRecaptcha) {
            return;
        }
        const mobile = phoneToString(values.mobile);
        setWorking(true);
        executeRecaptcha()
            .then((captcha) => {
                const payload: ContactRequest = {
                    captcha,
                    contact: {
                        mobile,
                        message: values.message,
                        name: values.name
                    },
                    locale: locale?.locale || elGR.locale,
                };
                return payload;
            })
            .then(data => {
                return axios.post<any, AxiosResponse>(`/api/launcher/contact`, data)
            })
            .then(() => {
                return message.success(t('contact.us.confirmation'))
            })
            .then(() => {
                form.resetFields();
                setWorking(false);
            })
            .catch(() => {
                setWorking(false);
                message.error('common.failure').then();
            });
    }, [executeRecaptcha, locale, setWorking]);

    const {t} = useTranslation();

    return (
      <LauncherLayout disableNavigation={true}>
          <LoadingOverlay working={working} />
          <Card title={t('contact.us.prompt')} style={{ maxWidth: 500, margin: '20px auto' }}>
              <Form
                  form={form}
                  name="contact"
                  layout="vertical"
                  onFinish={onFinish}
                  autoComplete="off"
              >
                  <Form.Item
                      label={<Label trl={"volunteer.form.fields.name"} /> }
                      name="name"
                      rules={[{ required: true, message: t('contact.us.validation.name') }]}
                  >
                      <Input prefix={<UserOutlined />} />
                  </Form.Item>

                  <Form.Item label={<Label trl="verification.form.mobile" />} name={"mobile"} required={true} rules={[{ required:true, message: ''}, {
                      validator: async (_, value: PhoneNumber) => {
                          if (phoneToString(value).length < 11) {
                              return  Promise.reject(t('phone.number.invalid'));
                          } else {
                              return  checkNumber(value, t);
                          }
                      }
                  }]}>
                      <PhoneInput />
                  </Form.Item>

                  <Form.Item
                      label={<Label trl="common.message" />}
                      name="message"
                      rules={[{ required: true, message: t('contact.us.validation.message') }]}
                  >
                      <TextArea rows={4} />
                  </Form.Item>

                  <Form.Item>
                      <Button type="primary" htmlType="submit" block>
                          {t('reservation.submit')}
                      </Button>
                  </Form.Item>
              </Form>
          </Card>
      </LauncherLayout>
    );
}